import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useThemeColors } from '../theme/ThemeContext';
import { parcelsAPI, feesAPI, applicationsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { 
  Package, 
  MapPin, 
  Calendar, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  User, 
  Star, 
  Weight, 
  Timer,
  Shield,
  PlaneTakeoff,
  ArrowLeft,
  Users,
  Send,
  X,
  FileText,
  ShoppingBasket
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const ParcelDetails = () => {
  const colors = useThemeColors();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [bidPrice, setBidPrice] = useState('');
  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: parcel, isLoading, error } = useQuery(
    ['parcel', id],
    async () => {
      const response = await parcelsAPI.getParcelDetails(id);
      console.log(response.data.data);
      
      return response.data.data;
    },
    {
      enabled: !!id,
    }
  );

  // Update fee estimate when bidPrice changes
  useEffect(() => {
    if (parcel && bidPrice && parseFloat(bidPrice) > 0 && parseFloat(bidPrice) < parseFloat(parcel.max_delivery_price)) {
      const calculateBidFee = async () => {
        setIsLoadingFee(true);
        try {
          const response = await feesAPI.calculateFeeEstimate('apply_to_parcel', {
            weight_kg: parseFloat(parcel.weight_kg),
            declared_value: parseFloat(parcel.declared_value) || 0,
            guaranteed_delivery: parcel.guaranteed_delivery,
            purchased: parcel.purchased,
            purchase_price: parseFloat(parcel.purchase_price) || 0,
            id: parcel.id,
            proposal_price: parseFloat(bidPrice)
          });
          
          setFeeEstimate(response.data.data);
          setIsLoadingFee(false);
        } catch (error) {
          console.error('Fee estimate error:', error);
          setIsLoadingFee(false);
        }
      };

      const timeoutId = setTimeout(calculateBidFee, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [bidPrice, parcel]);

  const handleSubmitApplication = async () => {
    if (!bidPrice || parseFloat(bidPrice) <= 0) {
      toast.error('Please enter a valid bid price');
      return;
    }

    if (parseFloat(bidPrice) > parseFloat(parcel.max_delivery_price)) {
      toast.error(`Bid cannot exceed ETB ${parcel.max_delivery_price}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await applicationsAPI.createApplication({
        target_id: parcel.id,
        target_type: 'parcel',
        proposal_price: parseFloat(bidPrice),
        total_cost: feeEstimate.total_cost
      });
      
      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      setBidPrice('');
      setFeeEstimate(null);
      
      // Optionally navigate to applications page
      // navigate('/applications');
    } catch (error) {
      console.error('Application submission error:', error);
      toast.error(error.response?.data?.message || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatValue = (value) => {
    if (!value) return 'N/A';
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
      maximumFractionDigits: 0
    }).format(num);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'posted':
        return { bg: colors.success[100], text: colors.success[800], border: colors.success[300] };
      case 'in_transit':
        return 'bg-theme-primary-100 text-theme-primary-800 border-theme-primary-200';
      case 'delivered':
        return 'bg-theme-neutral-100 text-theme-neutral-800 border-theme-neutral-200';
      case 'cancelled':
        return 'bg-theme-error-100 text-theme-error-800 border-theme-error-200';
      default:
        return 'bg-theme-neutral-100 text-theme-neutral-800 border-theme-neutral-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'posted':
        return <Clock className="h-5 w-5" />;
      case 'in_transit':
        return <Package className="h-5 w-5" />;
      case 'delivered':
        return <CheckCircle className="h-5 w-5" />;
      case 'cancelled':
        return <X className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'documents':
        return '📄';
      case 'electronics':
        return '📱';
      case 'clothing':
        return '👕';
      case 'food':
        return '🍔';
      default:
        return '📦';
    }
  };

  const handleApply = () => {
    setShowApplyModal(true);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mb-4"></div>
          <p className="text-theme-secondary font-medium">Loading parcel details...</p>
        </div>
      </div>
    );
  }

  if (error || !parcel || parcel.status === `expired`) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-theme-error/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-10 w-10" style={{ color: colors.error[600] }} />
            </div>
            <h1 className="text-2xl font-bold text-theme-primary mb-4">Parcel Not Found</h1>
            <p className="text-theme-secondary mb-8 max-w-md mx-auto">
              The parcel you're looking for doesn't exist or has been removed.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-theme-primary text-white px-8 py-3 rounded-xl font-medium hover:bg-theme-primary-hover transition-all duration-200 flex items-center mx-auto"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const daysUntilExpiry = parcel.expires_at 
    ? Math.ceil((new Date(parcel.expires_at) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <SEO 
          title={`Parcel ${parcel?.tracking_number || 'Details'}`} 
          description={`Track parcel ${parcel?.tracking_number || ''} from ${parcel?.origin || 'origin'} to ${parcel?.destination || 'destination'} on TripTrot`}
          keywords={['parcel tracking', 'package delivery', 'shipping details', 'parcel status']}
          canonical={`/parcels/${id}`}
          structuredData={{
            "@context": "https://schema.org",
            "@type": "ParcelDelivery",
            "trackingNumber": parcel?.tracking_number || "",
            "originAddress": parcel?.origin || "",
            "destinationAddress": parcel?.destination || "",
            "deliveryStatus": parcel?.status || "",
            "description": `Parcel delivery from ${parcel?.origin || 'origin'} to ${parcel?.destination || 'destination'}`
          }}
        />

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-theme-secondary hover:text-theme-primary transition-colors mb-6 group"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Parcels</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="space-y-6">

          {/* Route Overview Card */}
          <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-theme-primary flex items-center">
                <MapPin className="h-6 w-6 mr-3" style={{ color: colors.primary[500] }} />
                Route Overview
              </h2>
            </div>

            {/* Modern Route Display */}
            <div className="relative">
              <div className="flex items-center justify-between">
                {/* Origin */}
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-theme-success/20 to-theme-success/10 rounded-2xl mb-3 border border-theme-success/20">
                    <MapPin className="h-8 w-8" style={{ color: colors.success[600] }} />
                  </div>
                  <h3 className="font-bold text-theme-primary text-lg mb-1">{parcel.pickup}</h3>
                  <p className="text-sm text-theme-secondary">{parcel.pickup_country || 'Ethiopia'}</p>
                </div>

                {/* Flight Path with Arrow */}
                <div className="flex-1 flex items-center justify-center px-4">
                  <div className="flex items-center w-full max-w-xs">
                    <div className="flex-1 h-px bg-gradient-to-r from-theme-success/30 to-theme-primary/50"></div>
                    <div className="mx-3 p-2 bg-theme-primary/10 rounded-full border border-theme-primary/20">
                      <Package className="h-6 w-6" style={{ color: colors.primary[600] }} />
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-l from-theme-error/30 to-theme-primary/50"></div>
                  </div>
                </div>

                {/* Destination */}
                <div className="flex-1 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-theme-error/20 to-theme-error/10 rounded-2xl mb-3 border border-theme-error/20">
                    <MapPin className="h-8 w-8" style={{ color: colors.error[600] }} />
                  </div>
                  <h3 className="font-bold text-theme-primary text-lg mb-1">{parcel.dropoff}</h3>
                  <p className="text-sm text-theme-secondary">{parcel.dropoff_country || 'Ethiopia'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Trip Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Parcel Details Card */}
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                <Package className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Parcel Details
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                  <Package className="h-7 w-7 mx-auto mb-2" style={{ color: colors.primary[600] }} />
                  <div className="text-xs text-theme-secondary uppercase tracking-wide mb-1">Weight</div>
                  <div className="text-2xl font-bold text-theme-primary">{parcel.weight_kg}</div>
                  <div className="text-xs text-theme-secondary">kg</div>
                </div>

                <div className="text-center p-4 bg-gradient-to-br from-theme-success/10 to-theme-success/5 rounded-xl border border-theme-success/20">
                  <DollarSign className="h-7 w-7 mx-auto mb-2" style={{ color: colors.success[600] }} />
                  <div className="text-xs text-theme-secondary uppercase tracking-wide mb-1">
                    {parcel.purchased ? 'Purchase Price' : 'Declared Value'}
                  </div>
                  <div className="text-2xl font-bold text-theme-success">
                    {parcel.purchased
                      ? (parcel.purchase_price ? `${parcel.purchase_price}` : 'N/A')
                      : (parcel.declared_value ? `${parcel.declared_value}` : 'N/A')
                    }
                  </div>
                  <div className="text-xs text-theme-secondary">ETB</div>
                </div>

                <div className="text-center p-4 bg-gradient-to-br from-theme-warning/10 to-theme-warning/5 rounded-xl border border-theme-warning/20">
                  <Calendar className="h-7 w-7 mx-auto mb-2" style={{ color: colors.warning[600] }} />
                  <div className="text-xs text-theme-secondary uppercase tracking-wide mb-1">Expires</div>
                  <div className="text-2xl font-bold text-theme-warning">
                    {daysUntilExpiry !== null ? daysUntilExpiry : 'N/A'}
                  </div>
                  <div className="text-xs text-theme-secondary">days</div>
                </div>

                <div className="text-center p-4 bg-gradient-to-br from-theme-neutral/10 to-theme-neutral/5 rounded-xl border border-theme-neutral/20">
                  <Package className="h-7 w-7 mx-auto mb-2" style={{ color: colors.neutral[600] }} />
                  <div className="text-xs text-theme-secondary uppercase tracking-wide mb-1">Category</div>
                  <div className="text-2xl font-bold text-theme-primary capitalize">{parcel.category}</div>
                  <div className="text-xs text-theme-secondary">type</div>
                </div>
              </div>
            </div>

            {/* Services & Features Card */}
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                <Shield className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Services & Features
              </h3>

              <div className="space-y-4">
                {parcel.guaranteed_delivery && (
                  <div className="p-4 bg-gradient-to-r from-theme-success/10 to-theme-success/5 rounded-xl border border-theme-success/20">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-theme-success/20 rounded-lg flex items-center justify-center border border-theme-success/30">
                          <CheckCircle className="h-5 w-5" style={{ color: colors.success[600] }} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-theme-primary mb-1">Guaranteed Delivery</div>
                        <div className="text-sm text-theme-secondary">Sender requires an insurance amount of {parcel.declared_value}</div>
                      </div>
                    </div>
                  </div>
                )}
                {!parcel.guaranteed_delivery && (
                  <div className="p-4 bg-gradient-to-r from-theme-success/10 to-theme-success/5 rounded-xl border border-theme-success/20">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-theme-success/20 rounded-lg flex items-center justify-center border border-theme-success/30">
                          <CheckCircle className="h-5 w-5" style={{ color: colors.success[600] }} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-theme-primary mb-1">Standard Delivery</div>
                        <div className="text-sm text-theme-secondary">Sender does not require an insurance</div>
                      </div>
                    </div>
                  </div>
                )}

                {parcel.purchased && (
                  <div className="p-4 bg-gradient-to-r from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-theme-primary/20 rounded-lg flex items-center justify-center border border-theme-primary/30">
                          <ShoppingBasket className="h-5 w-5" style={{ color: colors.primary[600] }} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-theme-primary mb-1">Purchase Service</div>
                        <div className="text-sm text-theme-secondary">The traveller will purchase this item</div>
                      </div>
                    </div>
                  </div>
                )}
                {!parcel.purchased && (
                  <div className="p-4 bg-gradient-to-r from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-theme-primary/20 rounded-lg flex items-center justify-center border border-theme-primary/30">
                          <ShoppingBasket className="h-5 w-5" style={{ color: colors.primary[600] }} />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-theme-primary mb-1">No Purchase Service</div>
                        <div className="text-sm text-theme-secondary">The traveller handles only the shipping</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Additional Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Sender Information */}
            {parcel.sender && (
              <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
                <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                  <User className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                  Sender Information
                </h3>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-theme-primary/20 to-theme-primary/10 rounded-xl flex items-center justify-center border-2 border-theme-primary/20">
                      <User className="h-8 w-8" style={{ color: colors.primary[600] }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-theme-primary text-xl mb-2">{parcel.sender.name}</h4>
                    <div className="flex flex-wrap gap-3">
                      <div className="flex items-center px-3 py-1 bg-theme-warning/10 rounded-full border border-theme-warning/20">
                        <Star className="h-4 w-4 mr-1" style={{ color: colors.warning[500] }} />
                        <span className="text-sm font-medium text-theme-primary">
                          {parcel.sender.reputation_score || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Receiver Information */}
            {(parcel.receiver_name || parcel.receiver_phone) && (
              <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
                <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                  <User className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                  Receiver Information
                </h3>

                <div className="space-y-4">
                  {parcel.receiver_name && (
                    <div className="flex items-center justify-between p-3 bg-theme-neutral/5 rounded-lg">
                      <span className="text-sm text-theme-secondary">Name</span>
                      <span className="font-medium text-theme-primary">{parcel.receiver_name}</span>
                    </div>
                  )}
                  {parcel.receiver_phone && (
                    <div className="flex items-center justify-between p-3 bg-theme-neutral/5 rounded-lg">
                      <span className="text-sm text-theme-secondary">Phone</span>
                      <span className="font-medium text-theme-primary">{parcel.receiver_phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Parcel Analytics */}
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-6 flex items-center">
                <Users className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Parcel Analytics
              </h3>

              <div className="space-y-4">
                <div className="text-center p-4 bg-gradient-to-br from-theme-primary/10 to-theme-primary/5 rounded-xl border border-theme-primary/20">
                  <div className="text-3xl font-bold text-theme-primary mb-1">{parcel.applications_count || 0}</div>
                  <div className="text-xs text-theme-secondary uppercase tracking-wide font-medium">Applications</div>
                  <div className="text-xs text-theme-secondary mt-1">Delivery offers</div>
                </div>
              </div>
            </div>

          </div>

          {/* Description */}
          {parcel.notes && (
            <div className="bg-theme-card rounded-2xl border border-theme-light p-6 shadow-sm">
              <h3 className="text-lg font-bold text-theme-primary mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" style={{ color: colors.primary[500] }} />
                Description & Notes
              </h3>
              <div className="p-4 bg-theme-neutral/5 rounded-lg border border-theme-light">
                <p className="text-theme-secondary leading-relaxed text-sm">{parcel.notes}</p>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 w-full">

            {parcel.status === 'posted' && parcel.sender?.id !== user?.id && (
              <button
                onClick={handleApply}
                className="w-full bg-theme-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-theme-primary-hover transition-colors duration-200 flex items-center shadow-md hover:shadow-lg justify-center border border-theme-primary hover:border-theme-primary-hover"
              >
                <Send className="h-4 w-4 mr-2" />
                <span>Apply to Deliver</span>
              </button>
            )}
          </div>
        </div>
        {/* Apply Modal */}
        {showApplyModal && parcel && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-theme-card rounded-lg max-w-2xl w-full border-theme-light border max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-theme-primary">Apply to Deliver Parcel</h3>
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="text-theme-secondary hover:text-theme-primary transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-6">
                  <h4 className="font-medium text-theme-primary mb-2">{parcel.pickup} → {parcel.dropoff}</h4>
                  <p className="text-theme-secondary text-sm">
                    {parcel.category} parcel • {parcel.weight_kg}kg • Expires in {daysUntilExpiry} days
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="rounded-lg p-4" style={{ backgroundColor: colors.neutral[50] }}>
                    <h4 className="font-medium mb-2" style={{ color: colors.text.primary }}>Application Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: colors.text.secondary }}>Weight:</span>
                        <span style={{ color: colors.text.primary }}>{parcel.weight_kg} kg</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.text.secondary }}>
                          {parcel.purchased ? 'Purchase Price:' : 'Purchased:'}
                        </span>
                        <span style={{ color: colors.text.primary }}>
                          {parcel.purchased 
                            ? (parcel.purchase_price ? `ETB ${parcel.purchase_price}` : 'N/A')
                            : (parcel.purchased ? 'Yes' : 'No')
                          }
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: colors.text.secondary }}>
                          {parcel.guaranteed_delivery ? 'Declared Value:' : 'Guaranteed Delivery:'}
                        </span>
                        <span style={{ color: colors.text.primary }}>
                          {parcel.guaranteed_delivery 
                            ? (parcel.declared_value ? `ETB ${parcel.declared_value}` : 'N/A')
                            : (parcel.guaranteed_delivery ? 'Yes' : 'No')
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="rounded-lg p-4" style={{ backgroundColor: colors.warning[50] }}>
                    <h4 className="font-medium mb-2" style={{ color: colors.text.primary }}>Application Details</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: colors.text.primary }}>
                          Your Delivery Price (ETB) *
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={bidPrice}
                            onChange={(e) => setBidPrice(e.target.value)}
                            max={parcel.max_delivery_price}
                            min={1}
                            step="1"
                            className="w-full px-3 py-2 pr-16 border rounded-md focus:ring-2 focus:ring-opacity-50"
                            style={{ 
                              borderColor: colors.border,
                              focusRingColor: colors.primary[500]
                            }}
                            placeholder={`Up to ETB ${parcel.max_delivery_price}`}
                          />
                          <div className="absolute right-3 top-2.5 flex items-center text-sm" style={{ color: colors.text.tertiary }}>
                            <DollarSign className="h-4 w-4" />
                          </div>
                        </div>
                        <p className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
                          Maximum allowed: ETB {parcel.max_delivery_price}
                        </p>
                      </div>
                      
                      <div className="rounded-lg p-3" style={{ backgroundColor: colors.neutral[50] }}>
                        <h5 className="font-medium mb-2" style={{ color: colors.text.primary }}> Competition Info</h5>
                        <div className="space-y-2 text-sm" style={{ color: colors.text.secondary }}>
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: colors.success[500] }}></div>
                            <span>Competitive bidding - Best price wins!</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: colors.primary[500] }}></div>
                            <span>Lower bids increase your chances</span>
                          </div>
                          <div className="flex items-center">
                            <div className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: colors.warning[500] }}></div>
                            <span>Sender reviews all applications</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="border-t pt-3 mt-3" style={{ borderColor: colors.warning[200] }}>
                      <h5 className="font-medium mb-2" style={{ color: colors.text.primary }}> Fee Breakdown</h5>
                      {isLoadingFee ? (
                        <div className="text-sm" style={{ color: colors.text.secondary }}>Loading fee information...</div>
                      ) : feeEstimate && (
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm" style={{ color: colors.text.secondary }}>Application Fee:</span>
                            <span className="font-medium" style={{ color: colors.text.primary }}>
                              ETB {feeEstimate.application_fee || 0}
                            </span>
                          </div>
                          {feeEstimate.deposit_requirement !== null && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm" style={{ color: colors.text.secondary }}>Deposit Required:</span>
                              <span className="font-medium" style={{ color: colors.text.primary }}>
                                ETB {feeEstimate.deposit_requirement}
                              </span>
                            </div>
                          )}
                          {feeEstimate.proposal_price !== null && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm" style={{ color: colors.text.secondary }}>Your Bid:</span>
                              <span className="font-medium" style={{ color: colors.text.primary }}>
                                ETB {feeEstimate.proposal_price}
                              </span>
                            </div>
                          )}
                          {feeEstimate.total_cost !== null && (
                            <div className="flex justify-between items-center">
                              <span className="text-sm" style={{ color: colors.text.secondary }}>Total Cost:</span>
                              <span className="font-medium" style={{ color: colors.text.primary }}>
                                ETB {feeEstimate.total_cost}
                              </span>
                            </div>
                          )}
                          {/* <div className="flex justify-between items-center pt-2 border-t" style={{ borderColor: colors.warning[200] }}>
                            <span className="font-medium" style={{ color: colors.text.primary }}>You'll Receive:</span>
                            <span className="font-bold text-lg" style={{ color: colors.success[600] }}>
                              ETB {feeEstimate.you_receive || (feeEstimate.proposal_price - feeEstimate.total_fee)}
                            </span>
                          </div> */}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowApplyModal(false)}
                    className="flex-1 py-2 px-4 rounded-lg transition-colors"
                    style={{ backgroundColor: colors.neutral[200], color: colors.text.primary }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.neutral[300]}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.neutral[200]}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={isLoadingFee || isSubmitting}
                    onClick={handleSubmitApplication}
                    className="flex-1 py-2 px-4 rounded-lg text-white transition-colors"
                    style={{ backgroundColor: colors.primary[600] }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = colors.primary[700]}
                    onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary[600]}
                  >
                    {isSubmitting ? 'Submitting...' : isLoadingFee ? 'Calculating...' : 'Submit Application'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParcelDetails;
